<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - Delivery Times</template>

                <template #actions>
                    <jet-button type="button" class="h-7 leading-none" @click="showSlotModal = 'add'" >
                        Add Time Slot
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4">
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-7" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <th class="text-left">Time from</th>
                                <th class="text-left">Time to</th>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="label">Label</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="sort">Order</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="is_active">Active</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="delivery_today">Delivery today</sortable-header>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="slot in slots.data" :key="slot.id" class="cursor-pointer">
                                <td class="text-left">
                                    {{ slot.from }}
                                </td>
                                <td class="text-left">
                                    {{ slot.to }}
                                </td>
                                <td class="text-left">
                                    {{ slot.label }}
                                </td>
                                <td class="text-left">
                                    {{ slot.sort }}
                                </td>
                                <td class="text-left">
                                    <toggle
                                        v-model="slot.is_active"
                                        @click="changeSetting(slot, 'change_delivery_time_status', 'is_active')"
                                        :key="slot.is_active"
                                    />
                                </td>
                                <td class="text-left">
                                    <toggle
                                        v-model="slot.delivery_today"
                                        @click="changeSetting(slot, 'change_delivery_today', 'delivery_today')"
                                        :key="slot.delivery_today"
                                    />
                                </td>
                                <td class="text-right">
                                    <jet-danger-button class="mx-4" @click="deleteTimeSlot(slot)">
                                        Delete
                                    </jet-danger-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-3/4">
                    <pagination class="mt-0 mb-0" :links="slots.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="mainForm.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>

            <!-- Add time slot -->
            <jet-dialog-modal :show="showSlotModal" @close="closeSlotModal">
                <template #title>
                     <h3 class="text-2xl">Add Time Slot</h3>
                </template>

                <template #content>
                    <form @submit.prevent="submitForm" class="my-6">
                        <div class="form-group">
                            <jet-label value="Label:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.label" type="text" class="w-full"/>
                            <jet-input-error :message="form.errors.label" />
                        </div>

                        <div class="form-group">
                            <jet-label value="Order:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.sort" type="text" class="w-full" />
                            <jet-input-error :message="form.errors.sort" />
                        </div>

                        <div class="grid grid-cols-2 gap-2">
                            <div>
                                <jet-label value="From:" for="from" />
                                <jet-input id="from" type="time" v-model="form.time_start" class="w-full" />
                            </div>

                            <div>
                                <jet-label value="To:" for="to" class="col-span-6" />
                                <jet-input id="to" type="time" v-model="form.time_end" class="w-full" />
                            </div>
                        </div>

                        <div class="grid grid-cols-2 gap-2 form-group">
                            <div>
                                <jet-label value="Active" for="active" class="mr-2 inline-block" />
                                <toggle id="active" v-model="form.active" />
                            </div>

                            <div>
                                <jet-label value="Delivery today" for="delivery-today" class="mr-2 inline-block" />
                                <toggle id="delivery-today" v-model="form.delivery_today" />
                            </div>
                        </div>

                        <input type="submit" class="hidden" ref="submitFormButton" />
                    </form>
                </template>

                <template #footer>
                    <jet-button class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="closeSlotModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Time Slot -->
            <jet-confirmation-modal :show="showDeleteModal" @close="cancelDeleteTimeSlot()">
                <template #title>
                    Delete Time Slot
                </template>

                <template #content>
                    Are you sure you would like to delete time slot?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeleteTimeSlot">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="cancelDeleteTimeSlot()">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';
import Toggle from '@vueform/toggle';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        const form = useForm({
            id: null,
            active: true,
            label: '',
            sort: null,
            time_start: '08:00',
            time_end: '10:00',
            delivery_today: false,
        });

        return { mainForm, form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
        Toggle,
    },

    props: [
        'slots',
        'search',
        'sortColumn',
        'sortDirection',
        'paginate',
    ],

    data() {
        return {
            showSlotModal: false,
            showDeleteModal: false,

            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.mainForm.get(route('settings.delivery_times'), { preserveState: true });
        }, 250),


        changeSetting(item, route, param) {
            this.$inertia.put(this.route(route, { slot: item.id }), { param: item[param] }, {});
        },

        handleReset () {
            this.mainForm.search = '';
            this.mainForm.sortColumn = 'sort';
            this.mainForm.sortDirection = 'asc';
            this.handleSubmit();
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },

        closeSlotModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showSlotModal = false;
        },

        submitForm() {
            this.form.post(this.route('settings.add_delivery_time'), {
                onSuccess: () => {
                    this.closeSlotModal();
                }
            });
        },

        deleteTimeSlot(slot) {
            this.form.id = slot.id;
            this.showDeleteModal = true;
        },

        cancelDeleteTimeSlot() {
            this.form.reset();
            this.showDeleteModal = false;
        },

        handleDeleteTimeSlot() {
            this.form.delete(this.route('settings.delete_delivery_time', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.showDeleteModal = false;
                }
            });
        },
    },
}
</script>

<style scoped>
</style>
